import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Phone, Mail, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';

const SafeguardingAdults = () => {
  const principles = [
    {
      title: 'Empowerment',
      description: 'Supporting people to make informed choices'
    },
    {
      title: 'Prevention',
      description: 'Acting early to reduce risk'
    },
    {
      title: 'Proportionality',
      description: 'Responding appropriately to risk'
    },
    {
      title: 'Protection',
      description: 'Supporting those at greatest risk'
    },
    {
      title: 'Partnership',
      description: 'Working with local authorities and professionals'
    },
    {
      title: 'Accountability',
      description: 'Clear governance and responsibility'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7] py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <Shield className="w-16 h-16 text-[#4A90E2] mx-auto" />
            <h1 className="heading-hero">Safeguarding Adults</h1>
            <p className="body-large">
              Fully committed to safeguarding adults at risk and promoting their safety, dignity, and wellbeing at all times.
            </p>
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <p className="body-large text-center mb-8">
              Three Spheres Healthcare is fully committed to safeguarding adults at risk and promoting their safety, dignity, and wellbeing at all times.
            </p>
            <p className="body-medium text-gray-700 text-center">
              We recognise that <strong>safeguarding is everyone's responsibility</strong>. All staff working with or on behalf of Three Spheres Healthcare receive safeguarding training appropriate to their role and are expected to act immediately on any concerns.
            </p>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="heading-2 mb-12 text-center">Our Safeguarding Principles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {principles.map((principle, index) => (
              <Card key={index} className="p-6 border-0 hover:shadow-lg transition-all">
                <h3 className="font-semibold text-lg mb-3 text-[#4A90E2]">{principle.title}</h3>
                <p className="text-sm text-gray-700">{principle.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Raising Concerns */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 mb-8 text-center">Raising a Safeguarding Concern</h2>
            <p className="body-medium text-gray-700 text-center mb-8">
              Safeguarding concerns can be raised by service users, families, professionals, or members of the public.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <Card className="p-8 border-0 bg-[#E8F4FC] text-center">
                <Mail className="w-12 h-12 text-[#4A90E2] mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-3">Email</h3>
                <a 
                  href="mailto:info@threesphereshealthcare.org"
                  className="text-[#4A90E2] hover:underline break-all"
                >
                  info@threesphereshealthcare.org
                </a>
              </Card>
              
              <Card className="p-8 border-0 bg-[#E8F4FC] text-center">
                <Phone className="w-12 h-12 text-[#4A90E2] mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-3">Phone</h3>
                <a 
                  href="tel:03300433693"
                  className="text-[#4A90E2] hover:underline text-lg"
                >
                  0330 043 3693
                </a>
              </Card>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
              <div className="flex items-start gap-4">
                <AlertTriangle className="text-red-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-red-900">Emergency</h3>
                  <p className="text-sm text-red-800">
                    In an emergency or where there is immediate danger, please contact <strong>999</strong>.
                  </p>
                </div>
              </div>
            </div>

            <p className="body-medium text-gray-700 text-center mt-8">
              All concerns are taken seriously and escalated in line with local authority safeguarding procedures.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-gradient-to-r from-[#4A90E2] to-[#7B68BE] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="heading-2 text-white">Questions About Safeguarding?</h2>
            <p className="body-large text-white/90">
              Contact us for more information about our safeguarding policies and procedures.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#4A90E2] hover:bg-gray-100">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SafeguardingAdults;
